"use client";

import Link from "next/link";
import { motion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, ChevronRight, ExternalLink } from "@/components/icons";
import { type PRIMARY_CARDS, type SECONDARY_CARDS } from "./data";

export function PrimaryCard({
  icon,
  title,
  description,
  cta,
  href,
  accent,
  index,
}: (typeof PRIMARY_CARDS)[0] & { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="bg-surface-1 group border-foreground/[0.08] relative flex flex-col overflow-hidden rounded-2xl border p-6"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--foreground) 5%, transparent), transparent 70%)",
        }}
        aria-hidden="true"
      />

      <div className="mb-4 flex items-center gap-2.5">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border"
          style={{
            backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)`,
            borderColor: `color-mix(in srgb, ${accent} 20%, transparent)`,
            color: accent,
          }}
        >
          {icon}
        </div>
        <h2 className="text-foreground text-base font-semibold">{title}</h2>
      </div>

      <p className="text-muted-foreground mb-6 flex-1 text-sm leading-relaxed">{description}</p>

      <Link
        href={href}
        className="border-border bg-card text-foreground hover:bg-muted inline-flex items-center gap-1.5 self-start rounded-lg border px-4 py-2 text-sm font-semibold transition-colors duration-200"
      >
        {cta}
        <ChevronRight size={14} />
      </Link>
    </motion.div>
  );
}

export function SecondaryCard({
  title,
  description,
  cta,
  href,
  external,
  index,
}: (typeof SECONDARY_CARDS)[number] & { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={scrollRevealViewport}
      transition={{ duration: 0.45, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <h3 className="text-foreground mb-2 text-lg font-semibold">{title}</h3>
      <p className="text-muted-foreground mb-3 text-sm leading-relaxed">{description}</p>

      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="text-foreground hover:text-primary inline-flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200"
      >
        {cta}
        {external ? (
          <ExternalLink size={13} className="text-muted-foreground" />
        ) : (
          <ArrowRight size={13} />
        )}
      </a>
    </motion.div>
  );
}
